import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Package, ShareNetwork, Key, EnvelopeSimple, SignOut, Buildings,
  CalendarBlank, ArrowLeft, ArrowSquareOut, IdentificationBadge,
  Eye, EyeSlash, Copy, Crown,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { itApi, STAFF_STATUS_META, ASSET_STATUS_META, EMAIL_STATUS_META } from './itApi';
import OffboardingDialog from './OffboardingDialog';
import { formatDate } from '@/utils/datetime';

const fmtDate = (s) => (s ? formatDate(s) : '—');

function avatarText(name = '') {
  const n = (name || '').trim();
  if (/^[\x00-\x7F\s]+$/.test(n)) {
    const t = n.split(/\s+/).filter(Boolean);
    return ((t[0]?.[0] || '') + (t[1]?.[0] || '')).toUpperCase() || '?';
  }
  return n.replace(/\s/g, '').slice(0, 2) || '?';
}

export const ResourceCard = ({ icon: Icon, title, count, accent, testid, bodyClass = '', children }) => (
  <div data-testid={testid} className="bg-white border border-zinc-200 rounded-xl shadow-sm p-5 flex flex-col transition-all duration-200 hover:shadow-md">
    <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-zinc-200">
      <span className={`flex items-center justify-center w-9 h-9 rounded-lg ${accent}`}><Icon size={19} weight="duotone" /></span>
      <h3 className="font-semibold text-zinc-800">{title}</h3>
      <span className="ml-auto text-sm font-bold text-zinc-900 tabular-nums">{count}</span>
    </div>
    <div className={`flex-1 ${bodyClass}`}>{children}</div>
  </div>
);

const Empty = ({ label }) => <p className="text-xs text-zinc-400 py-6 text-center">{label}</p>;

const copyText = async (v) => {
  try { await navigator.clipboard.writeText(v); toast.success('コピーしました'); }
  catch { toast.error('コピーに失敗しました'); }
};

// Reveal/hide an encrypted password on demand (audited server-side).
export function Credential({ hasPassword, onReveal, testid, emptyLabel = 'パスワード未設定' }) {
  const [pw, setPw] = useState(undefined); // undefined = hidden, string = revealed
  const [busy, setBusy] = useState(false);
  if (!hasPassword) return <span className="text-[11px] text-zinc-400" data-testid={`${testid}-nopw`}>{emptyLabel}</span>;
  const toggle = async () => {
    if (pw !== undefined) { setPw(undefined); return; }
    setBusy(true);
    try { setPw((await onReveal()) || ''); }
    catch { toast.error('パスワードの取得に失敗しました'); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      {pw !== undefined ? (
        <>
          <span className="font-mono text-xs bg-zinc-100 rounded px-2 py-0.5" data-testid={`${testid}-pw`}>{pw || '（空）'}</span>
          {pw && <button onClick={() => copyText(pw)} data-testid={`${testid}-copy`} className="text-zinc-400 hover:text-teal-700" title="コピー"><Copy size={14} /></button>}
          <button onClick={toggle} data-testid={`${testid}-hide`} className="text-zinc-400 hover:text-zinc-700" title="隠す"><EyeSlash size={14} /></button>
        </>
      ) : (
        <button onClick={toggle} disabled={busy} data-testid={`${testid}-reveal`} className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-teal-700 disabled:opacity-50">
          <Eye size={13} /> パスワード表示
        </button>
      )}
    </span>
  );
}

export default function StaffProfile({ employeeId, staffList, onBack, onChanged }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offboardOpen, setOffboardOpen] = useState(false);

  const load = useCallback(() => {
    if (!employeeId) return;
    setLoading(true);
    itApi.staffProfile(employeeId).then(setP).catch(() => setP(null)).finally(() => setLoading(false));
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  if (loading || !p) {
    return <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-12 text-center text-sm text-zinc-400" data-testid="staff-profile-loading">読み込み中…</div>;
  }

  const sm = STAFF_STATUS_META[p.status] || STAFF_STATUS_META.active;
  const totalResources = p.assets.length + p.ips.length + p.license_seats.length + p.emails.length;

  return (
    <div className="space-y-5" data-testid="staff-profile">
      {/* Header */}
      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-5 md:p-6">
        <button onClick={onBack} className="md:hidden inline-flex items-center gap-1 text-sm text-zinc-500 mb-3" data-testid="staff-profile-back">
          <ArrowLeft size={16} /> 一覧へ
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-teal-700 text-white text-lg font-bold shrink-0 shadow-sm">
              {avatarText(p.name)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold tracking-tight text-zinc-900 truncate" data-testid="staff-profile-name">{p.name}</h2>
                <Badge variant="outline" className={sm.cls} data-testid="staff-profile-status">{sm.label}</Badge>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 flex-wrap">
                <span className="inline-flex items-center gap-1"><Buildings size={14} /> {p.department_name || '部署未設定'}</span>
                {p.code && <span className="inline-flex items-center gap-1"><IdentificationBadge size={14} /> {p.code}</span>}
                <span className="inline-flex items-center gap-1"><CalendarBlank size={14} /> 入社 {fmtDate(p.join_date)}</span>
                {p.leave_date && <span className="inline-flex items-center gap-1 text-rose-500">退職 {fmtDate(p.leave_date)}</span>}
              </div>
            </div>
          </div>
          {p.status === 'active' && (
            <Button variant="outline" data-testid="staff-offboard-btn" onClick={() => setOffboardOpen(true)}
              className="h-10 rounded-lg border-rose-200 text-rose-600 hover:bg-rose-50">
              <SignOut size={17} className="mr-1.5" weight="bold" /> オフボーディング
            </Button>
          )}
        </div>
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <ResourceCard icon={Package} title="貸与資産" count={p.assets.length} accent="bg-blue-50 text-blue-600" testid="profile-card-assets">
          {p.assets.length === 0 ? <Empty label="割当資産はありません" /> : (
            <ul className="space-y-2">
              {p.assets.map((a) => {
                const asm = ASSET_STATUS_META[a.status] || ASSET_STATUS_META.in_stock;
                return (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-zinc-100 px-3 py-2">
                    <span className="min-w-0">
                      <span className="font-medium text-zinc-800 truncate block">{a.name}</span>
                      <span className="text-[11px] text-zinc-400 font-mono">{a.asset_tag || '—'}{a.ip_address ? ` ・ ${a.ip_address}` : ''}</span>
                    </span>
                    <Badge variant="outline" className={`${asm.cls} shrink-0`}>{asm.label}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </ResourceCard>

        <ResourceCard icon={ShareNetwork} title="IPアドレス" count={p.ips.length} accent="bg-violet-50 text-violet-600" testid="profile-card-ips">
          {p.ips.length === 0 ? <Empty label="割当IPはありません" /> : (
            <ul className="space-y-2">
              {p.ips.map((ip) => (
                <li key={ip.id} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-zinc-100 px-3 py-2">
                  <span className="font-mono text-zinc-800">{ip.ip_address}</span>
                  <span className="text-[11px] text-zinc-400 truncate">{ip.device_name || ip.purpose || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </ResourceCard>

        <ResourceCard icon={Key} title="ライセンス座席" count={p.license_seats.length} accent="bg-amber-50 text-amber-600" testid="profile-card-licenses">
          {p.license_seats.length === 0 ? <Empty label="割当ライセンスはありません" /> : (
            <ul className="space-y-2">
              {p.license_seats.map((s) => (
                <li key={s.id} className="rounded-lg border border-zinc-100 px-3 py-2 text-sm" data-testid={`profile-license-${s.id}`}>
                  <span className="font-semibold text-zinc-800 block leading-snug">{s.license_name || '—'}</span>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0">{s.license_type_label || '—'}</Badge>
                    {s.is_admin && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0" data-testid={`profile-license-${s.id}-admin`}>
                        <Crown size={10} weight="fill" className="mr-0.5" /> 管理者
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
                    {s.login_email ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 min-w-0">
                        <EnvelopeSimple size={12} />
                        <span className="font-mono truncate">{s.login_email}</span>
                        <button onClick={() => copyText(s.login_email)} data-testid={`profile-license-${s.id}-email-copy`} className="text-zinc-400 hover:text-teal-700" title="コピー"><Copy size={12} /></button>
                      </span>
                    ) : <span className="text-[11px] text-zinc-400">ログインメール未設定</span>}
                    <Credential hasPassword={s.has_password} emptyLabel="パスワード未登録"
                      onReveal={() => itApi.revealSeat(s.license_id, s.id).then((r) => r.password)} testid={`profile-license-${s.id}`} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ResourceCard>

        <ResourceCard icon={EnvelopeSimple} title="メールアカウント" count={p.emails.length} accent="bg-emerald-50 text-emerald-600" testid="profile-card-emails">
          {p.emails.length === 0 ? <Empty label="割当メールはありません" /> : (
            <ul className="space-y-2">
              {p.emails.map((em) => {
                const esm = EMAIL_STATUS_META[em.status] || EMAIL_STATUS_META.active;
                return (
                  <li key={em.id} className="rounded-lg border border-zinc-100 px-3 py-2 text-sm" data-testid={`profile-email-${em.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-zinc-800 min-w-0">
                        <span className="truncate">{em.email}</span>
                        <button onClick={() => copyText(em.email)} data-testid={`profile-email-${em.id}-copy`} className="text-zinc-400 hover:text-teal-700 shrink-0" title="コピー"><Copy size={12} /></button>
                      </span>
                      <Badge variant="outline" className={`${esm.cls} shrink-0`}>{esm.label}</Badge>
                    </div>
                    <div className="mt-1.5">
                      <Credential hasPassword={em.has_password} onReveal={() => itApi.revealEmail(em.id).then((r) => r.password)} testid={`profile-email-${em.id}`} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </ResourceCard>
      </div>

      <div className="flex items-center justify-between text-xs text-zinc-400 px-1">
        <span>保有リソース 合計 {totalResources} 件</span>
        <button onClick={() => navigate('/it/assets')} className="inline-flex items-center gap-1 hover:text-teal-700" data-testid="staff-profile-manage-assets">
          各モジュールで管理 <ArrowSquareOut size={13} />
        </button>
      </div>

      <OffboardingDialog
        open={offboardOpen}
        onClose={() => setOffboardOpen(false)}
        profile={p}
        staffList={staffList}
        onDone={() => { setOffboardOpen(false); load(); onChanged?.(); }}
      />
    </div>
  );
}
